import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FingoWatermarkLogo from '../../../assets/icons/logos/fingo_watermark';
import LazyLoadImage from '../../../components/images/LazyLoadImage';
import ProgressBarStepper from '../../../components/steppers/progressBarStepper/ProgressBarStepper';
import ACCEPT_TERMS from '../../../graphql/user/accept-terms';
import useGetUser from '../../../hooks/useGetUser';
import FingoCircularProgressLoader from '../../../components/loader/FingoCircularProgressLoader';
import STEP_TITLE_BY_KEY from '../constants/stepTitles';
import TermsTyCStep from '../components/TermsTyCStep';
import TermsCredentialsStep from '../components/TermsCredentialsStep';
import CannotAcceptTyCDialog from '../components/CannotAcceptTyCDialog';

const TermsView = ({
  credentialsSource,
  cannotAcceptDescription,
  privacyPolicyUrl,
  enableStepper,
}) => {
  const HOME_PATH = '/app/sales';
  const history = useHistory();
  const goHome = useCallback(() => history.replace(HOME_PATH), [history]);
  const user = useGetUser();
  const [openCannotAcceptTyCDialog, setOpenCannotAcceptTyCDialog] = useState(false);
  const selectedCompany = user?.selectedCompany;
  const hasTerms = selectedCompany?.hasLendingAcceptedterms;
  const hasCredentials = selectedCompany?.hasSiiCredentials;
  const isReady = hasTerms !== undefined && hasCredentials !== undefined;

  const [acceptTermsAndConditions, { loading: loadingAcceptTerms }] = useMutation(ACCEPT_TERMS, {
    onError: () => setOpenCannotAcceptTyCDialog(true),
  });

  const steps = useMemo(() => {
    if (!isReady) return [];
    const updatedSteps = [];
    if (hasTerms === false) updatedSteps.push('terms');
    if (hasCredentials === false) updatedSteps.push('credentials');
    return updatedSteps;
  }, [hasTerms, hasCredentials, isReady]);

  const [initialTotalSteps] = useState(() => {
    if (!isReady) return null;
    return steps.length;
  });

  const currentStep = steps[0];
  const completedSteps = Math.max(0, initialTotalSteps - steps.length);
  const currentStepNumber = Math.min(completedSteps + 1, initialTotalSteps);

  const completeCurrentStep = useCallback(() => {
    if (steps.length <= 1) goHome();
  }, [steps.length, goHome]);

  const handleAcceptAndContinue = useCallback(() => {
    const companyId = user?.selectedCompany?.id;
    if (!companyId) return;

    acceptTermsAndConditions({
      variables: {
        companyId: Number(companyId),
      },
      onCompleted: () => completeCurrentStep(),
    });
  }, [acceptTermsAndConditions, user?.selectedCompany?.id, completeCurrentStep]);

  const [showStepper] = useState(() => !hasTerms && !hasCredentials);

  if (!selectedCompany || !isReady) return <FingoCircularProgressLoader showEmptyLogo />;
  if (steps.length === 0) {
    goHome();
    return <FingoCircularProgressLoader showEmptyLogo />;
  }

  return (
    <Stack px={20} py={5} spacing={4} height="90vh">
      {enableStepper && showStepper && (
        <ProgressBarStepper
          currentStep={currentStepNumber}
          totalSteps={initialTotalSteps}
          stepTitle={STEP_TITLE_BY_KEY[currentStep]}
          progress={initialTotalSteps > 0 ? (completedSteps / initialTotalSteps) * 100 : 0}
          showBackButton={false}
          showNextLabel={false}
        />
      )}

      <Typography variant="large_1" fontWeight={700} color="gray.A700">
        Bienvenido a tu portal de financiamiento de facturas
      </Typography>

      <Stack flex={1} minHeight={0}>

        {currentStep === 'terms' && (
          <TermsTyCStep
            onNext={handleAcceptAndContinue}
            isSubmitting={loadingAcceptTerms}
            privacyPolicyUrl={privacyPolicyUrl}
          />
        )}

        {currentStep === 'credentials' && (
          <TermsCredentialsStep
            onCompleted={completeCurrentStep}
            source={credentialsSource}
          />
        )}
      </Stack>

      <CannotAcceptTyCDialog
        open={openCannotAcceptTyCDialog}
        onRetry={() => {
          setOpenCannotAcceptTyCDialog(false);
          handleAcceptAndContinue();
        }}
        onClose={() => setOpenCannotAcceptTyCDialog(false)}
        description={cannotAcceptDescription}
      />

      <Stack alignItems="flex-end">
        <LazyLoadImage
          src={FingoWatermarkLogo}
          alt="fingo watermark"
          width={119}
          format="webp"
        />
      </Stack>
    </Stack>
  );
};

TermsView.propTypes = {
  credentialsSource: PropTypes.string,
  cannotAcceptDescription: PropTypes.string.isRequired,
  privacyPolicyUrl: PropTypes.string,
  enableStepper: PropTypes.bool,
};

TermsView.defaultProps = {
  credentialsSource: null,
  privacyPolicyUrl: null,
  enableStepper: true,
};

export default TermsView;
